import { useCallback, useEffect, useState } from 'react';
import { RiRefreshLine } from 'react-icons/ri';
import classNames from 'styles/utils/classNames';
import { useMarketSessionPhase } from 'hooks/useMarketSessionClock';

const DEFAULT_COOLDOWN_MS = 5 * 60 * 1000;

interface Props {
  onRefresh?: () => void;
  isAutoRefreshing?: boolean;
  cooldownMs?: number;
  className?: string;
}

export default function FlowRefreshLink({
  onRefresh,
  isAutoRefreshing = false,
  cooldownMs = DEFAULT_COOLDOWN_MS,
  className,
}: Props) {
  const [cooldownEndAt, setCooldownEndAt] = useState(() => Date.now() + cooldownMs);
  const [nowMs, setNowMs] = useState(() => Date.now());
  // The one app-wide session clock (see MarketDataStatus for the hydration rationale), so this pill
  // and the header status dot read the same session and can never disagree. `null` until read on
  // the client, which behaves as "pending".
  const sessionPhase = useMarketSessionPhase();

  const handleClick = useCallback(() => {
    if (!onRefresh || Date.now() < cooldownEndAt) return;

    onRefresh();
    setCooldownEndAt(Date.now() + cooldownMs);
  }, [cooldownEndAt, cooldownMs, onRefresh]);

  useEffect(() => {
    if (cooldownEndAt <= Date.now()) return;

    const id = setInterval(() => {
      const t = Date.now();
      setNowMs(t);
      if (t >= cooldownEndAt) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [cooldownEndAt]);

  /*
    AUTO-REFRESH IS A CLAIM, AND IT HAS A CLOCK.

    This pill used to render whenever `isAutoRefreshing` was passed — which both call sites do
    unconditionally for a subscriber — while the flow behind it only refetches during market hours.
    At 2am on a Saturday it animated a green "Live" over a feed that had not moved since Friday,
    eight pixels from a header that correctly said the market was closed.

    It reads the same session clock as the header status, so the two can never disagree. Outside the
    cash session it falls through to the manual refresh control when there is one, and otherwise
    renders nothing — an auto-refresh surface with no manual handler has no live claim to make once
    the tape stops, and the status dot beside it already explains why.
  */
  const isCashSession = sessionPhase === 'regular';

  if (isAutoRefreshing && isCashSession) {
    return (
      <span
        className={classNames(
          'inline-flex h-5 items-center gap-1 whitespace-nowrap px-0.5 text-[10px] font-medium leading-none text-text-faded',
          className,
        )}
        title="Flow updates automatically during market hours"
        aria-label="Flow updates automatically during market hours"
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-green-secondary opacity-30" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-green-secondary" />
        </span>
        Live
      </span>
    );
  }

  // Everything below is the manual-refresh affordance — a button, then its cooldown countdown.
  // Without a handler there is nothing here to offer: returning null keeps a subscriber's
  // auto-refresh pill from decaying into a stuck "Refresh in 0:00" once the pill above is gone.
  if (!onRefresh) return null;

  const remainingMs = Math.max(0, cooldownEndAt - nowMs);
  const canRefresh = remainingMs <= 0;

  if (canRefresh) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={classNames(
          'inline-flex items-center gap-1 whitespace-nowrap rounded border border-brand-primary/35 bg-brand-primary/10 px-2 py-1 text-[11px] font-semibold text-brand-primary transition-colors hover:bg-brand-primary/20',
          className,
        )}
        aria-label="Refresh flow"
      >
        <RiRefreshLine className="text-xs" />
        <span>Refresh flow</span>
      </button>
    );
  }

  const seconds = Math.ceil(remainingMs / 1000);
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toString().padStart(2, '0');

  return (
    <span
      className={classNames(
        'inline-flex items-center whitespace-nowrap rounded border border-palette-border bg-palette-black-3 px-2 py-1 text-[11px] text-text-faded',
        className,
      )}
      aria-live="polite"
    >
      Refresh in {m}:{s}
    </span>
  );
}
